import geolib from 'geolib'
import Utils from 'lib/utils'
import { hasLocizeTranslation } from 'lib/utils/locize'
import StateStorage from 'lib/state-storage'
import {
  segmentFindItemStoreListViewed,
  segmentFindItemStoreSelected,
} from './segment'
import { noSizeSelectNotice } from '../add-to-bag-cta/utils'
import { CTA_KEYS } from '../add-to-bag-cta/const'

/**
 * Gets the available store information based on the selected size option.
 *
 * @param {Object} params - Function parameters
 * @param {Object} params.selectedOption - The selected size option
 * @param {Function} params.setStoreAvailable - Function to set available stores
 * @param {Array} params.stockInfo - Array of store stock information
 */
export const getAvailableStoreFromSizeOption = ({
  selectedOption,
  setStoreAvailable,
  stockInfo,
}) => {
  const availableStock = []

  stockInfo.forEach((store) =>
    store.product_info?.forEach((size_info) => {
      if (
        size_info.product_id_attribute === selectedOption.id_product_attribute
      ) {
        availableStock.push({ ...store, available_stock: size_info })
      }
    }),
  )

  setStoreAvailable(availableStock)
}

/**
 * @param {Object} availableStock - The available stock for a product
 * @param {Object} t - The localization function
 * @returns {string|null} The stock badge text or null
 */
export const getStockBadge = (availableStock, t) => {
  switch (availableStock?.badge) {
    case 'last_unit_left':
      return hasLocizeTranslation(t, 'STOCK_ONE_LEFT', 'ONLY 1 LEFT')
    case 'low_stock':
      return hasLocizeTranslation(t, 'STOCK_SELL_FAST', 'SELLING FAST!')
    default:
      return null
  }
}

/**
 * Gets store location information sorted by distance from search location.
 *
 * @param {Object} params - Function parameters
 * @param {Object} params.geoData - Geolocation data from search
 * @param {number} params.geoData.latitude - Latitude from search
 * @param {number} params.geoData.longitude - Longitude from search
 * @param {Function} params.setStoreAvailableFromSearch - Function to set sorted store data
 * @param {Array} params.stockInfo - Array of store location data
 */
export const getStoreLocationFromSearch = ({
  geoData,
  setStoreAvailableFromSearch,
  stockInfo,
}) => {
  if (geoData) {
    const locationFromSearch = {
      latitude: geoData.latitude,
      longitude: geoData.longitude,
    }

    let parsedPins = stockInfo?.map((store_location) => {
      const distanceFromSearch =
        geolib.getDistance(locationFromSearch, {
          latitude: store_location.latitude,
          longitude: store_location.longitude,
        }) / 1000
      return { ...store_location, distance_from_user: distanceFromSearch }
    })

    parsedPins = Utils.sortPomeloPins(parsedPins)
    setStoreAvailableFromSearch(parsedPins)
  } else {
    setStoreAvailableFromSearch(stockInfo)
  }
}

/**
 * Get the store IDs from the stock info array
 *
 * @param {Object[]} stockInfo - Array of store stock info objects
 * @returns {string[]} Array of store ID strings
 */
const getStoreIds = (stockInfo) =>
  stockInfo?.map((store) => store.id_ns_location?.toString())

/**
 * @param {object} product - The product object
 * @param {function} setOptionMessage - Function to set the notice text
 * @param {function} t - Translation function
 */
const handleDisplayNoticeText = (product, setOptionMessage, t) => {
  const isSoldOut = product?.stock?.is_sold_out
  const noticeLocize = isSoldOut ? CTA_KEYS.notifyMe : CTA_KEYS.addToBag

  noSizeSelectNotice(noticeLocize, setOptionMessage, t)
}

/**
 * Handle opening the store modal
 *
 * @param {Object} params - Function parameters
 * @param {Object} params.product - Product object
 * @param {Function} params.setShouldOpenStoreModal - Setter function to open modal
 * @param {Function} params.setOptionMessage - Setter function to open message
 * @param {Object[]} params.stockInfo - Array of store stock info objects
 * @param {Object} params.selectedOption - Selected product variant
 * @param {Function} params.t - Translation function
 */
export const handleOpenStoreModal = ({
  product,
  setShouldOpenStoreModal,
  setOptionMessage,
  stockInfo,
  selectedOption,
  t,
}) => {
  if (!selectedOption) {
    handleDisplayNoticeText(product, setOptionMessage, t)
  } else {
    const user = StateStorage.getAuthUser()

    setShouldOpenStoreModal(true)
    segmentFindItemStoreListViewed({
      id_customer: user?.id_customer,
      id_product: product.id_product,
      id_product_attribute: selectedOption.id_product_attribute,
      store_ids: getStoreIds(stockInfo),
    })
  }
}

/**
 * Track store accordion open event
 *
 * @param {Object} params - Function parameters
 * @param {boolean} params.isAccordionOpen - Whether accordion is open
 * @param {string} params.productID - Product ID
 * @param {Object} params.store - Store object
 */
export const trackStoreAccordionOpen = ({
  isAccordionOpen,
  productID,
  store,
}) => {
  const user = StateStorage.getAuthUser()

  if (isAccordionOpen) {
    segmentFindItemStoreSelected({
      id_customer: user?.id_customer,
      id_product: productID,
      id_product_attribute: store.available_stock.product_id_attribute,
      store_id: store.id_ns_location,
    })
  }
}

/**
 * @param {string} href - The URL to open
 */
export const handleStoreButtonClick = (href) => {
  window?.open(href, '_blank').focus()
}

/**
 * @param {Object} store - Store object
 * @returns {boolean} True if floor level and nearest station is present
 */
export const shouldDisplayDotValue = (store) =>
  store.floor_level && store.nearest_station

export default { handleStoreButtonClick }
